import { Link, useParams } from "react-router-dom";
import { Installation, Integration, ProviderInfo } from "data/api";
import { useListIntegrations } from "src/hooks/useListIntegrations";
import { useProviders } from "src/hooks/useProviders";
import { MainPageTemplate } from "src/layout/SidebarLayout/MainPageLayout/MainPageTemplate";
import { capitalizeFirstLetter } from "src/util";

import { Loading } from "components/Loading";
import { Pill } from "components/Pill";
import { TableComponent } from "components/TableComponent";
import { Monospace } from "components/Typography/Monospace";

import { useListAllInstallations } from "../useListAllInstallations";

type CustomerDetailsContentProps = {
  customerInstallations: Array<Installation>;
  integrations: Array<Integration> | undefined;
  providers: Record<string, ProviderInfo> | undefined;
  projectId: string;
};

const CustomerDetailsContent = ({
  customerInstallations,
  integrations,
  providers,
  projectId,
}: CustomerDetailsContentProps) => {
  if (!customerInstallations.length) {
    return (
      <div className="p-4 text-center text-gray-600">
        No installations found for this customer.
      </div>
    );
  }

  return (
    <>
    <TableComponent.Table>
      <TableComponent.TableHeader>
        <TableComponent.TableRow>
          <TableComponent.TableHeaderCell>
            Provider
          </TableComponent.TableHeaderCell>
          <TableComponent.TableHeaderCell>
            Integration
          </TableComponent.TableHeaderCell>
          <TableComponent.TableHeaderCell>
            Installation ID
          </TableComponent.TableHeaderCell>
          <TableComponent.TableHeaderCell>
            Status
          </TableComponent.TableHeaderCell>
          <TableComponent.TableHeaderCell>
            Created
          </TableComponent.TableHeaderCell>
        </TableComponent.TableRow>
      </TableComponent.TableHeader>
      <TableComponent.TableBody>
        {customerInstallations.map((installation) => {
          const integration = integrations?.find(
            (i) => i.id === installation.integrationId
          );
          const providerName = integration?.provider;

          const providerDisplayName =
            providers?.[providerName || ""]?.displayName ||
            capitalizeFirstLetter(providerName || "");

          return (
            <TableComponent.TableRow key={installation.id}>
              <TableComponent.TableCell>
                {providerDisplayName}
              </TableComponent.TableCell>
              <TableComponent.TableCell>
                  <Monospace>{integration?.name || integration?.id}</Monospace>
              </TableComponent.TableCell>
              <TableComponent.TableCell>
                <Link
                  to={`/projects/${projectId}/integrations/${installation.integrationId}/installations/${installation.id}`}
                  className="text-slate-900 underline"
                >
                  <Monospace>{installation.id}</Monospace>
                </Link>
              </TableComponent.TableCell>
              <TableComponent.TableCell>
                <Pill
                  color={
                    installation.healthStatus === "healthy"
                      ? "green"
                      : "red"
                  }
                  text={
                    installation.healthStatus === "healthy"
                      ? "Healthy"
                      : "Unhealthy"
                  }
                />
              </TableComponent.TableCell>
              <TableComponent.TableCell>
                {new Date(installation.createTime).toLocaleDateString()}
              </TableComponent.TableCell>
            </TableComponent.TableRow>
          );
        })}
      </TableComponent.TableBody>
      
    </TableComponent.Table>
    <TableComponent.TableFooterDiv>
       Total Installations: {customerInstallations.length}
    </TableComponent.TableFooterDiv>
    </>
  );
};

export const CustomerDetailsPage = () => {
  const { projectId, groupRef } = useParams();
  const {
    data: installations,
    isLoading: isLoadingInstallations,
    isError: isErrorInstallations,
  } = useListAllInstallations();
  const { data: integrations, isLoading: isLoadingIntegrations } =
    useListIntegrations();
  const { data: providers } = useProviders();

  const customerInstallations =
    installations?.filter(
      (installation) => installation.group?.groupRef === groupRef
    ) || [];

  const customerName = customerInstallations[0]?.group?.groupName || groupRef;

  const isLoading = isLoadingInstallations || isLoadingIntegrations;
  const isError = isErrorInstallations;

  // Create breadcrumb links
  const links = [
    {
      text: "Customers",
      href: `/projects/${projectId}/customers`,
    },
  ];

  if (!projectId) {
    return <div>No project ID found</div>;
  }

  return (
    <MainPageTemplate title={`Customer: ${customerName}`} links={links}>
      {isError && <p className="p-4">There was an error loading the customer details.</p>}
      {isLoading && (
        <div className="flex justify-center">
          <Loading />
        </div>
      )}
      {!isLoading && !isError && (
        <CustomerDetailsContent
          customerInstallations={customerInstallations}
          integrations={integrations}
          providers={providers}
          projectId={projectId}
        />
      )}
    </MainPageTemplate>
  );
};
